import { GlobalConfigType } from 'actions'
import { LayoutLoading } from 'components/LayoutLoading'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { getFinancialStatements } from 'services'
import { svgLoading } from 'stories/assets'
import { formatDate, thousandSeperator } from 'utils'

interface IStatementAccount {
  accountNo: number
  accountNumber: string
  accountName: string
  balance: number
  amount: number
}

interface ISectionGroup {
  section: string
  accounts: IStatementAccount[]
  total: number
}

interface IFinancialStatementsData {
  asOfDate: string
  incomeStatement: {
    revenueSections: ISectionGroup[]
    expenseSections: ISectionGroup[]
    totalRevenue: number
    totalExpenses: number
    netIncome: number
  }
  balanceSheet: {
    assetSections: ISectionGroup[]
    liabilitySections: ISectionGroup[]
    equitySections: ISectionGroup[]
    totalAssets: number
    totalLiabilities: number
    equityFromAccounts: number
    currentPeriodEarnings: number
    totalEquity: number
    totalLiabilitiesAndEquity: number
    inBalance: boolean
    balanceMessage: string
  }
}

const formatAmount = (value: number) => {
  if (!value) return '-'
  return `$${thousandSeperator(value)}`
}

const SectionHeader = ({ title }: { title: string }) => (
  <tr className="bg-gray-200 text-gray-800 border-b border-gray-300">
    <td colSpan={2} className="px-3 py-2 font-semibold uppercase tracking-wide">
      {title}
    </td>
  </tr>
)

const SubSectionHeader = ({ title }: { title: string }) => (
  <tr className="bg-gray-100 text-gray-700 border-b border-gray-200">
    <td colSpan={2} className="px-3 py-2 font-medium">
      {title}
    </td>
  </tr>
)

const NestedSectionHeader = ({ title }: { title: string }) => (
  <tr className="bg-gray-50 text-gray-600 border-b border-gray-100">
    <td colSpan={2} className="px-3 py-1.5 pl-5 text-sm">
      {title}
    </td>
  </tr>
)

const AccountRows = ({ accounts }: { accounts: IStatementAccount[] }) => (
  <>
    {accounts.map((account) => (
      <tr key={account.accountNo} className="border-b hover:bg-gray-50">
        <td className="px-3 py-2 pl-6">{account.accountName}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">{formatAmount(account.amount)}</td>
      </tr>
    ))}
  </>
)

const SectionGroups = ({ sections, showSectionHeaders }: { sections: ISectionGroup[]; showSectionHeaders: boolean }) => (
  <>
    {sections.map((group) => (
      <Fragment key={group.section}>
        {showSectionHeaders && <NestedSectionHeader title={group.section} />}
        <AccountRows accounts={group.accounts} />
      </Fragment>
    ))}
  </>
)

const TotalRow = ({
  label,
  value,
  className = 'bg-gray-50 text-gray-800',
}: {
  label: string
  value: number
  className?: string
}) => (
  <tr className={`font-semibold border-t border-gray-200 ${className}`}>
    <td className="px-3 py-2">{label}</td>
    <td className="px-3 py-2 text-right whitespace-nowrap">{formatAmount(value)}</td>
  </tr>
)

export const FinancialStatements = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState<IFinancialStatementsData | null>(null)

  const sectionOptions = useSelector((state: any) => state.globalConfigReducer[GlobalConfigType.ACCOUNT_SECTIONS] || [])

  const sectionOrder = useMemo(() => {
    const configured = sectionOptions
      .filter((option: { visible?: boolean; value?: string | null }) => option.visible !== false && option.value)
      .map((option: { value: string }) => option.value)
    return [...configured, 'Uncategorized']
  }, [sectionOptions])

  const sortSections = (sections: ISectionGroup[]) => {
    const orderMap = new Map(sectionOrder.map((section, index) => [section, index]))
    return [...sections].sort((a, b) => {
      const aIndex = orderMap.get(a.section) ?? Number.MAX_SAFE_INTEGER
      const bIndex = orderMap.get(b.section) ?? Number.MAX_SAFE_INTEGER
      if (aIndex !== bIndex) return aIndex - bIndex
      return a.section.localeCompare(b.section)
    })
  }

  useEffect(() => {
    fetch()
  }, [])

  const fetch = async () => {
    setLoading(true)
    try {
      const result = await getFinancialStatements()
      setData(result)
    } catch (error) {
      console.error('Error fetching financial statements:', error)
      setData(null)
    }
    setLoading(false)
  }

  const incomeStatement = data?.incomeStatement
  const balanceSheet = data?.balanceSheet
  const revenueSections = sortSections(incomeStatement?.revenueSections || [])
  const expenseSections = sortSections(incomeStatement?.expenseSections || [])
  const assetSections = sortSections(balanceSheet?.assetSections || [])
  const liabilitySections = sortSections(balanceSheet?.liabilitySections || [])
  const equitySections = sortSections(balanceSheet?.equitySections || [])

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-3 items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center pb-2">
          Financial Statements
          {isLoading && (
            <span className="ml-3">
              <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
            </span>
          )}
        </h1>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Income Statement and Balance Sheet, derived from the Trial Balance. Period-to-date.
        {data?.asOfDate && (
          <span className="ml-1 font-medium">As of {formatDate(data.asOfDate)} (all transactions through today).</span>
        )}
      </p>

      <div className="relative overflow-auto">
        <LayoutLoading show={isLoading} />

        <div className="sm:rounded-lg mb-8 overflow-x-auto max-w-3xl border border-gray-200">
          <table className="w-full text-sm text-left text-gray-700">
            <tbody>
              <SectionHeader title="Income Statement (period to date)" />

              <SubSectionHeader title="Revenue" />
              {revenueSections.length ? (
                <SectionGroups sections={revenueSections} showSectionHeaders={revenueSections.length > 1} />
              ) : (
                <tr>
                  <td colSpan={2} className="px-3 py-2 pl-6 text-gray-500">
                    No revenue accounts
                  </td>
                </tr>
              )}
              <TotalRow label="Total Revenue" value={incomeStatement?.totalRevenue || 0} />

              <tr>
                <td colSpan={2} className="h-2" />
              </tr>

              <SubSectionHeader title="Cost of Sales & Expenses" />
              {expenseSections.length ? (
                <SectionGroups sections={expenseSections} showSectionHeaders={expenseSections.length > 1} />
              ) : (
                <tr>
                  <td colSpan={2} className="px-3 py-2 pl-6 text-gray-500">
                    No expense accounts
                  </td>
                </tr>
              )}
              <TotalRow label="Total Cost of Sales & Expenses" value={incomeStatement?.totalExpenses || 0} />
              <TotalRow
                label="Net Income"
                value={incomeStatement?.netIncome || 0}
                className="bg-green-50 text-gray-800 border-t-2 border-green-200"
              />

              <tr>
                <td colSpan={2} className="h-6" />
              </tr>

              <SectionHeader title="Balance Sheet (as of period end)" />

              <SubSectionHeader title="Assets" />
              {assetSections.length ? (
                <SectionGroups sections={assetSections} showSectionHeaders={assetSections.length > 1} />
              ) : (
                <tr>
                  <td colSpan={2} className="px-3 py-2 pl-6 text-gray-500">
                    No asset accounts
                  </td>
                </tr>
              )}
              <TotalRow label="Total Assets" value={balanceSheet?.totalAssets || 0} />

              <tr>
                <td colSpan={2} className="h-2" />
              </tr>

              <SubSectionHeader title="Liabilities" />
              {liabilitySections.length ? (
                <SectionGroups sections={liabilitySections} showSectionHeaders={liabilitySections.length > 1} />
              ) : (
                <tr>
                  <td colSpan={2} className="px-3 py-2 pl-6 text-gray-500">
                    No liability accounts
                  </td>
                </tr>
              )}
              <TotalRow label="Total Liabilities" value={balanceSheet?.totalLiabilities || 0} />

              <tr>
                <td colSpan={2} className="h-2" />
              </tr>

              <SubSectionHeader title="Equity" />
              {equitySections.length ? (
                <SectionGroups sections={equitySections} showSectionHeaders={equitySections.length > 1} />
              ) : null}
              <tr className="border-b hover:bg-gray-50">
                <td className="px-3 py-2 pl-6">Current period earnings (not yet closed)</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {formatAmount(balanceSheet?.currentPeriodEarnings || 0)}
                </td>
              </tr>
              <TotalRow label="Total Equity" value={balanceSheet?.totalEquity || 0} />
              <TotalRow
                label="Total Liabilities + Equity"
                value={balanceSheet?.totalLiabilitiesAndEquity || 0}
                className="bg-blue-50 text-gray-800 border-t-2 border-blue-200"
              />

              <tr>
                <td colSpan={2} className="px-3 py-3">
                  <span
                    className={
                      balanceSheet?.inBalance ? 'text-green-600 font-medium' : 'text-red-500 font-medium'
                    }
                  >
                    {balanceSheet?.balanceMessage || ''}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
